import React, { useState } from 'react';
import { Button, ButtonGroup, Grid2, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useGetCoupons } from '@openapi/coupons/coupons';
import { useGetShow } from '@openapi/shows/shows';
import useNotify from '../../hooks/useNotify';
import useSorting from '../../hooks/useSorting';
import { TABLE_ROW_COUNT_DEFAULT } from '../../helpers/constants';
import PageWithTitle from '../../components/atom/PageWithTitle';
import CouponsTable from './components/CouponsTable';
import SearchInput from '../../components/atom/SearchInput';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../../hooks/useCapabilities';

const CouponsPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { notifyError } = useNotify();
    const { showId } = useParams();
    const [{ limit, page }, setParams] = useState({ limit: TABLE_ROW_COUNT_DEFAULT, page: 0 });
    const { sort, sortDir, setSorting } = useSorting(['created_at', 'DESC']);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const { can, CAPABILITIES } = useCapabilities();
    const canManageCoupons = can(CAPABILITIES.COUPONS_MANAGE);
    const {
        data,
        isLoading: loading,
        _error,
        refetch: reloadData,
    } = useGetCoupons(
        {
            showId: Number(showId),
            code: search || undefined,
            page,
            pageSize: limit,
            sort,
            sortDir,
        },
        {
            query: {
                onError: notifyError,
            },
        },
    );

    const {
        data: show,
        isLoading: loadingShow,
        error: _errorShow,
    } = useGetShow(Number(showId), {
        query: {
            onError: notifyError,
        },
    });

    const handleExport = async () => {
        if (!data?.coupons?.length) return;
        const XLSX = await import('xlsx');

        const exportData = data.coupons.map((coupon) => ({
            Code: coupon.code,
            'Discount Type': coupon.discountType,
            'Discount Value':
                coupon.discountType === 'percentage'
                    ? `${coupon.discountValue}%`
                    : `$${(coupon.discountValue / 100).toFixed(2)}`,
            'Expiration Date': coupon.expirationDate,
            Status: coupon.status,
            'Current Uses': coupon.currentUses,
            'Max Uses': coupon.maxUses || 'Unlimited',
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Coupons');
        XLSX.writeFile(wb, `coupons-${show?.title || showId}.xlsx`);
    };

    return (
        <PageWithTitle
            title={`${t('coupons.title')} (${loadingShow ? '' : show?.title || ''})`}
            stackHeadline
            headline={[
                <Grid2
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    wrap="nowrap"
                    key="search-and-buttons"
                >
                    {!isMobile && (
                        <Grid2 sx={{ mr: 2 }}>
                            <SearchInput
                                search={search}
                                setSearch={setSearch}
                                setParams={setParams}
                                limit={limit}
                            />
                        </Grid2>
                    )}
                    <Grid2 sx={{ flexShrink: 0 }}>
                        <ButtonGroup variant="contained">
                            <Tooltip title={t('coupons.export')}>
                                <Button onClick={handleExport}>
                                    <DownloadIcon />
                                </Button>
                            </Tooltip>
                            {canManageCoupons && (
                                <Tooltip title={t('coupons.generate')}>
                                    <Button
                                        onClick={() => {
                                            navigate(`/shows/${showId}/coupons/new`);
                                        }}
                                    >
                                        <AddIcon />
                                    </Button>
                                </Tooltip>
                            )}
                        </ButtonGroup>
                    </Grid2>
                </Grid2>,
            ]}
        >
            {isMobile && (
                <Grid2 size={{ xs: 12 }}>
                    <SearchInput
                        search={search}
                        setSearch={setSearch}
                        setParams={setParams}
                        limit={limit}
                        fullWidth
                    />
                </Grid2>
            )}
            <CouponsTable
                rows={data?.coupons}
                limit={limit}
                page={page}
                setParams={setParams}
                total={data?.total}
                loading={loading}
                reloadData={reloadData}
                sort={sort}
                sortDir={sortDir}
                setSorting={setSorting}
            />
        </PageWithTitle>
    );
};

export default CouponsPage;
